import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'
import type {
  Citizen,
  CitizenSearchFilters,
  CitizenSearchResult,
  CitizenSearchSort,
  CitizenSearchState,
  SavedSearch,
} from '@/types/citizen.types'

const initialFilters: CitizenSearchFilters = {
  query: '',
  status: [],
  enrollmentStatus: [],
}

const initialSort: CitizenSearchSort = {
  field: 'createdAt',
  direction: 'desc',
}

const initialState: CitizenSearchState = {
  // Search results
  results: null,

  // Current filters
  filters: initialFilters,

  // Sorting
  sort: initialSort,

  // UI state
  isLoading: false,
  isSearching: false,

  // Selected citizen
  selectedCitizen: null,

  // View mode
  viewMode: 'table',

  // Advanced search
  showAdvancedSearch: false,

  // Export
  isExporting: false,

  // Error handling
  error: null,

  // Recent searches
  recentSearches: [],

  // Saved searches
  savedSearches: [],
}

const citizenSearchSlice = createSlice({
  name: 'citizenSearch',
  initialState,
  reducers: {
    // Search operations
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      state.isSearching = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setSearchResults: (state, action: PayloadAction<CitizenSearchResult>) => {
      state.results = action.payload
      state.isLoading = false
      state.isSearching = false
      state.error = null
    },

    setSearchError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
      state.isSearching = false
    },

    clearSearchResults: (state) => {
      state.results = null
      state.error = null
    },

    // Filter management
    updateFilters: (state, action: PayloadAction<Partial<CitizenSearchFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    resetFilters: (state) => {
      state.filters = initialFilters
      state.sort = initialSort
    },

    setQuickSearch: (state, action: PayloadAction<string>) => {
      state.filters = {
        ...initialFilters,
        query: action.payload,
      }
    },

    // Sorting
    updateSort: (state, action: PayloadAction<CitizenSearchSort>) => {
      state.sort = action.payload
    },

    toggleSortDirection: (state, action: PayloadAction<CitizenSearchSort['field']>) => {
      if (state.sort.field === action.payload) {
        state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        state.sort = {
          field: action.payload,
          direction: 'asc',
        }
      }
    },

    // Selected citizen
    setSelectedCitizen: (state, action: PayloadAction<Citizen | null>) => {
      state.selectedCitizen = action.payload
    },

    updateSelectedCitizen: (state, action: PayloadAction<Partial<Citizen>>) => {
      if (state.selectedCitizen) {
        state.selectedCitizen = {
          ...state.selectedCitizen,
          ...action.payload,
        }
      }
    },

    // UI state
    setViewMode: (state, action: PayloadAction<CitizenSearchState['viewMode']>) => {
      state.viewMode = action.payload
    },

    toggleAdvancedSearch: (state) => {
      state.showAdvancedSearch = !state.showAdvancedSearch
    },

    setAdvancedSearch: (state, action: PayloadAction<boolean>) => {
      state.showAdvancedSearch = action.payload
    },

    // Export operations
    setExporting: (state, action: PayloadAction<boolean>) => {
      state.isExporting = action.payload
    },

    // Recent searches
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const query = action.payload.trim()
      if (!query || state.recentSearches.includes(query)) return

      state.recentSearches = [query, ...state.recentSearches.slice(0, 9)] // Keep last 10
    },

    clearRecentSearches: (state) => {
      state.recentSearches = []
    },

    removeRecentSearch: (state, action: PayloadAction<string>) => {
      state.recentSearches = state.recentSearches.filter((search) => search !== action.payload)
    },

    // Saved searches
    addSavedSearch: (state, action: PayloadAction<SavedSearch>) => {
      const existingIndex = state.savedSearches.findIndex((s) => s.id === action.payload.id)
      if (existingIndex >= 0) {
        state.savedSearches[existingIndex] = action.payload
      } else {
        state.savedSearches.push(action.payload)
      }
    },

    removeSavedSearch: (state, action: PayloadAction<string>) => {
      state.savedSearches = state.savedSearches.filter((search) => search.id !== action.payload)
    },

    applySavedSearch: (state, action: PayloadAction<SavedSearch>) => {
      state.filters = action.payload.filters
      state.sort = action.payload.sort
    },

    // Bulk operations
    selectMultipleCitizens: (_state, _action: PayloadAction<string[]>) => {
      // This would be used for bulk operations UI state
      // Could add selectedCitizenIds to state if needed
    },

    // Pagination
    updatePage: (state, action: PayloadAction<number>) => {
      if (state.results) {
        state.results.page = action.payload
      }
    },

    // Clear all state
    resetSearchState: (state) => {
      return {
        ...initialState,
        recentSearches: state.recentSearches, // Keep recent searches
        savedSearches: state.savedSearches, // Keep saved searches
      }
    },
  },
})

// Export actions
export const {
  setSearchLoading,
  setSearchResults,
  setSearchError,
  clearSearchResults,
  updateFilters,
  resetFilters,
  setQuickSearch,
  updateSort,
  toggleSortDirection,
  setSelectedCitizen,
  updateSelectedCitizen,
  setViewMode,
  toggleAdvancedSearch,
  setAdvancedSearch,
  setExporting,
  addRecentSearch,
  clearRecentSearches,
  removeRecentSearch,
  addSavedSearch,
  removeSavedSearch,
  applySavedSearch,
  selectMultipleCitizens,
  updatePage,
  resetSearchState,
} = citizenSearchSlice.actions

// Selectors
export const selectCitizenSearch = (state: RootState) => state.citizenSearch
export const selectSearchResults = (state: RootState) => state.citizenSearch.results
export const selectSearchFilters = (state: RootState) => state.citizenSearch.filters
export const selectSearchSort = (state: RootState) => state.citizenSearch.sort
export const selectSelectedCitizen = (state: RootState) => state.citizenSearch.selectedCitizen
export const selectIsSearching = (state: RootState) => state.citizenSearch.isSearching
export const selectSearchError = (state: RootState) => state.citizenSearch.error
export const selectViewMode = (state: RootState) => state.citizenSearch.viewMode
export const selectShowAdvancedSearch = (state: RootState) => state.citizenSearch.showAdvancedSearch
export const selectRecentSearches = (state: RootState) => state.citizenSearch.recentSearches
export const selectSavedSearches = (state: RootState) => state.citizenSearch.savedSearches
export const selectIsExporting = (state: RootState) => state.citizenSearch.isExporting

// Computed selectors
export const selectHasSearchResults = (state: RootState) =>
  state.citizenSearch.results && state.citizenSearch.results.citizens.length > 0

export const selectTotalCitizens = (state: RootState) => state.citizenSearch.results?.total || 0

export const selectCurrentPage = (state: RootState) => state.citizenSearch.results?.page || 1

export const selectTotalPages = (state: RootState) => {
  const results = state.citizenSearch.results
  if (!results) return 0
  return Math.ceil(results.total / results.pageSize)
}

export const selectHasNextPage = (state: RootState) => state.citizenSearch.results?.hasMore || false

export const selectHasPreviousPage = (state: RootState) => {
  const results = state.citizenSearch.results
  return results ? results.page > 1 : false
}

export const selectActiveFiltersCount = (state: RootState) => {
  const filters = state.citizenSearch.filters
  let count = 0

  if (filters.query?.trim()) count++
  if (filters.firstName?.trim()) count++
  if (filters.lastName?.trim()) count++
  if (filters.phoneNumber?.trim()) count++
  if (filters.enrollmentId?.trim()) count++
  if (filters.region?.trim()) count++
  if (filters.zone?.trim()) count++
  if (filters.woreda?.trim()) count++
  if (filters.kebele?.trim()) count++
  if (filters.status && filters.status.length > 0) count++
  if (filters.enrollmentStatus && filters.enrollmentStatus.length > 0) count++
  if (filters.dateOfBirthFrom?.trim()) count++
  if (filters.dateOfBirthTo?.trim()) count++
  if (filters.createdFrom?.trim()) count++
  if (filters.createdTo?.trim()) count++
  if (filters.gender) count++
  if (filters.hasPhoto !== undefined) count++
  if (filters.hasDocuments !== undefined) count++
  if (filters.hasBiometrics !== undefined) count++
  if (filters.verificationStatus) count++

  return count
}

export const selectIsFilterActive = (state: RootState) => selectActiveFiltersCount(state) > 0

// Export reducer
export default citizenSearchSlice.reducer
